import { Box, Button, Flex, Heading, Image, Span, Text } from '@chakra-ui/react'
import { Tooltip } from '@/components/ui/tooltip'
import Icon from '@/components/shared/icons'
import { Link, useNavigate } from 'react-router-dom'
import { dashboardRoutes } from '../shared/navigation'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store/store'
import JwtPayload from '@/entities/api/jwt'
import { API_URL } from '@/services/api/keys'

import logo from '@/assets/img/logo.jpg'
import { roleConvert } from '@/utils/role-convert'
import logoutDashboard from '../shared/close'
import getPermissionsMap from '@/utils/permissions-convert'

const MenuRightLayout = ({
	menu,
	left,
	close,
	lefting,
	data,
}: {
	menu: boolean
	left: boolean
	close: () => void
	lefting: () => void
	data: JwtPayload
}) => {
	const { imageRefrest } = useSelector((state: RootState) => state.state)
	const permissions = getPermissionsMap(data?.permissions || [])
	const dispatch = useDispatch<AppDispatch>()
	const navigate = useNavigate()

	return (
		<Box
			w={{ base: menu ? '280px' : '0px', xl: left ? '70px' : '280px' }}
			h='100vh'
			position={{ base: 'fixed', xl: 'static' }}
			bg='white'
			overflow='hidden'
			transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
			boxShadow='0 4px 20px rgba(0, 0, 0, 0.08)'
			zIndex='999'
			borderRight='1px solid'
			borderColor='gray.100'
		>
			<Flex w='full' h='full' flexDirection='column' position='relative'>
				<Flex
					p='4'
					alignItems='center'
					justifyContent={left ? 'center' : 'space-between'}
					borderBottom='1px solid'
					borderColor='gray.100'
					minH='70px'
				>
					<Flex alignItems='center'>
						<Image
							src={logo}
							w='40px'
							h='40px'
							minW={left ? '40px' : 'auto'}
							minH={left ? '40px' : 'auto'}
							objectFit='cover'
							borderRadius='lg'
						/>
						{!left && (
							<Heading
								as='h4'
								ml='3'
								fontSize='lg'
								fontWeight='700'
								color='gray.800'
							>
								Dashboard
							</Heading>
						)}
					</Flex>

					<Box display={{ base: 'block', xl: 'none' }}>
						<Button
							variant='ghost'
							size='sm'
							onClick={close}
							borderRadius='lg'
							_hover={{ bg: 'gray.100' }}
						>
							<Icon icon='close' size='lg' color='gray.600' />
						</Button>
					</Box>
				</Flex>

				<Box flex='1' overflowY='auto' py='4'>
					<Flex flexDirection='column' px='2'>
						{dashboardRoutes.map((route, index) => {
							const hasPermission = route.value
								? permissions[route.value]?.view
								: true

							if (!hasPermission) return null

							const MenuItem = (
								<Link to={route.path} key={index} className='w-full'>
									<Flex
										cursor='pointer'
										borderRadius='xl'
										_hover={{
											bg: 'gray.50',
											transform: 'translateX(2px)',
										}}
										alignItems='center'
										justifyContent={left ? 'center' : 'flex-start'}
										p='3'
										mb='1'
										transition='all 0.2s ease'
										position='relative'
									>
										<Icon icon={route.icon} size='lg' color='gray.700' />
										{!left && (
											<Text
												ml='3'
												fontSize='md'
												fontWeight='600'
												color='gray.700'
											>
												{route.name}
											</Text>
										)}
									</Flex>
								</Link>
							)

							return left ? (
								<Tooltip
									key={index}
									content={route.name}
									positioning={{ placement: 'right' }}
									openDelay={300}
									closeDelay={100}
								>
									{MenuItem}
								</Tooltip>
							) : (
								MenuItem
							)
						})}

						<Box display={{ base: 'none', xl: 'block' }} mt='2'>
							{left ? (
								<Tooltip
									content='Expandir menú'
									positioning={{ placement: 'right' }}
									openDelay={300}
									closeDelay={100}
								>
									<Flex
										cursor='pointer'
										borderRadius='xl'
										_hover={{
											bg: 'teal.50',
											transform: 'translateX(2px)',
										}}
										alignItems='center'
										justifyContent='center'
										p='3'
										mb='1'
										transition='all 0.2s ease'
										onClick={lefting}
									>
										<Icon icon='right' size='lg' color='teal.600' />
									</Flex>
								</Tooltip>
							) : (
								<Flex
									cursor='pointer'
									borderRadius='xl'
									_hover={{
										bg: 'teal.50',
										transform: 'translateX(2px)',
									}}
									alignItems='center'
									p='3'
									mb='1'
									transition='all 0.2s ease'
									onClick={lefting}
								>
									<Icon icon='left' size='lg' color='black' />
									<Text ml='3' fontSize='md' fontWeight='600' color='gray.700'>
										Contraer
									</Text>
								</Flex>
							)}
						</Box>
					</Flex>
				</Box>

				<Box borderTop='1px solid' borderColor='gray.100' p='4'>
					<Link to='/account'>
						<Flex
							alignItems='center'
							justifyContent={left ? 'center' : 'flex-start'}
							cursor='pointer'
							borderRadius='xl'
							_hover={{ bg: 'gray.50' }}
							p='3'
							mb='3'
							transition='all 0.2s ease'
						>
							{data?.image?.url ? (
								<Image
									key={imageRefrest}
									src={`${API_URL}/${data.image?.url}`}
									w='40px'
									h='40px'
									minW={left ? '40px' : 'auto'}
									minH={left ? '40px' : 'auto'}
									borderRadius='full'
									cursor='pointer'
								/>
							) : (
								<Box
									w='40px'
									h='40px'
									borderRadius='full'
									bg='gray.200'
									display='flex'
									alignItems='center'
									justifyContent='center'
								>
									<Icon icon='user' size='lg' color='gray.600' />
								</Box>
							)}

							{!left && (
								<Box ml='3' flex='1'>
									<Text
										fontSize='sm'
										fontWeight='600'
										color='gray.800'
										lineHeight='1.2'
									>
										{data ? `${data?.name} ${data?.lastname}` : 'Sin nombre'}
									</Text>
									<Text fontSize='xs' color='gray.500' lineHeight='1.2'>
										{data?.position && roleConvert(data?.position)}
									</Text>
								</Box>
							)}
						</Flex>
					</Link>

					{left ? (
						<Tooltip
							content='Cerrar sesión'
							positioning={{ placement: 'right' }}
							openDelay={300}
							closeDelay={100}
						>
							<Button
								w='full'
								h='45px'
								borderRadius='xl'
								bg='red.50'
								color='red.600'
								border='1px solid'
								borderColor='red.200'
								_hover={{
									bg: 'red.100',
									borderColor: 'red.300',
								}}
								onClick={() => logoutDashboard(dispatch, navigate)}
							>
								<Icon icon='off' size='lg' />
							</Button>
						</Tooltip>
					) : (
						<Button
							w='full'
							h='45px'
							borderRadius='xl'
							bg='red.50'
							color='red.600'
							border='1px solid'
							borderColor='red.200'
							cursor='pointer'
							_hover={{
								bg: 'red.100',
								borderColor: 'red.300',
							}}
							onClick={() => logoutDashboard(dispatch, navigate)}
						>
							<Span mr='2' color='red' fontWeight='600'>
								Cerrar session
							</Span>
							<Icon icon='off' size='2xl' color='red' />
						</Button>
					)}
				</Box>
			</Flex>
		</Box>
	)
}

export default MenuRightLayout
